import StorageService from "@/services/Storage"

export type InstanceFeatureFlagType =
	"canForwardMessages" |
	"groupMentions" |
	"managerTagsIntegrationText" |
	"isAbleToSeeStatusMessageResponse" |
	"createChatBotByIntegration" |
	"datadogTracing" |
	"datadogLogs" |
	"canIgnoreMessageBlastAdvise" |
	"canShowExportMessageOption" |
	"canFilterOthersTabWithLastMessageTransactedIn4Hours" |
	"canFilterOthersTabWithLastMessageTransactedIn16Hours" |
	"canFilterOnGoingAndQueueTabWithLastMessageTransactedIn16Hours" |
	"canFilterOnGoingAndQueueTabWithLastMessageTransactedIn24Hours" |
	"canFilterOnGoingAndQueueTabWithLastMessageTransactedIn72Hours" |
	"canFilterOnGoingAndQueueTabWithLastMessageTransactedIn96Hours" |
	"canRetrySocketEventEmissionAfterReconnection" |
	"canViewThumbnail" |
	"canSeeMessageBlastTrigger" |
	"canSendExtraDataInTheSendWebhookBlock" |
	"playgroundAIBetaTesters" |
	"canViewQueueMetrics" |
	"canShowActiveCampaignBanner" |
	"canShowBulkContactsBanner" |
	"canShowAttendantsBanner" |
	"canShowICPBanner" |
	"canShowDefaultBanner" |
	"canShowMessagesBanner" |
	"canShowBotBanner" |
	"canShowPaymentRecurrenceMigrationBanner" |
	"canNotViewSearchBaseBanner" |
	"canDownloadReportFromMultiInstances" |
	"canViewPartnershipsPage" |
	"canReplyToButtons" |
	"canActivePushNotifications" |
	"canManageWABAChannelConnection" |
	"canShowWelcomePopup" |
	"canUseRolePermissions" |
	"canAutoFinishAttendancesOnUserDeletion" |
	"canUseWABATemplateCreditsLimit" |
	"canUseRDStationCRM" |
	"canViewTrackableLinkInteractionsFilters" |
	"canShowMerlinNonOwnerWarning" |
	"canUseBotFlowWABAErrorOutput" |
	"canShowWhatsappBlockAlertBanner" |
	"canUseOutboundWebhooks" |
	"canUseSuperlogicaBlock" |
	"canValidateWaitForClientResponseBlockVariableName" |
	"canUseAccurateAttendantFilterLogic"

export type AdminFeatureFlagType =
	"contactFlowHistory" |
	"copyRawInviteLink" |
	"instanceResponsiblePhoneNumber" |
	"canShowExportMessageOption" |
	"messageProcessingSteps" |
	"canConfigureWABAApiChannel" |
	"canIgnoreAttendantQuantityLimit" |
	"canImportBotsOnWaba" |
	"canAdminImportTags" |
	"canAdminImportQuickReplies" |
	"canViewAgentDebugInfo"

export type UserFeatureFlagType = "canImportTags" | "canImportQuickReplies" | "canViewUninassauFeaturePage"

type FeatureFlagMap = {
	instance: Record<InstanceFeatureFlagType, number[]>
	admin: Record<AdminFeatureFlagType, number[]>
	user: Record<UserFeatureFlagType, number[]>
}

export type FeatureFlag = InstanceFeatureFlagType | AdminFeatureFlagType | UserFeatureFlagType

const LETALK_WEB_TEST_INSTANCES = [8428, 8429, 8430, 7166, 7324, 7385, 17, 3046, 2245]
const LETALK_WABA_TEST_INSTANCES = [7166]
const PLAYGROUND_AI_BETA_TESTERS_INSTANCES = [
	8402,
	8351,
	8327,
	8325,
	8297,
	8280,
	8206,
	8075,
	8018,
	7967,
	7925,
	7644,
	7388,
	7349,
	6930,
	6863,
	6756,
	6615,
	6520,
	3918,
	3890,
	3841,
	3355,
	2091,
	4525,
	8335,
	3624,
	2046,
	6890,
	7382,
	8394,
	8266,
	8267,
	8028,
	7432,
	8032,
	7880,
	8397,
	8363,
	7978,
	7982,
	7525,
	7541
]

const INSTANCES_WITH_ACTIVE_CAMPAIGN_BANNER: number[] = [
	7825,
	4676,
	3828,
	8110,
	6624,
	8176,
	4706,
	4968,
	4983,
	3067,
	5340,
	5220,
	3622,
	4650,
	7055,
	3072,
	4798,
	5847,
	7921,
	6411,
	2443,
	2354,
	2560,
	5008,
	8149,
	4710,
	5034,
	5141,
	5243,
	5348,
	5706,
	5965,
	6232,
	3420,
	6889,
	7068,
	7938,
	5317,
	3442
]

const INSTANCES_WITH_BULK_CONTACTS_BANNER: number[] = [
	5619,
	2966,
	5326,
	6383,
	7208,
	6385,
	4947,
	2652,
	6222,
	5616,
	5618,
	6382,
	6537,
	6900,
	5989,
	3503,
	5214,
	5211,
	5239,
	3818,
	5598,
	5523,
	5846,
	7538,
	2778,
	2399,
	7746,
	7452,
	3695,
	7440,
	3103,
	3691,
	6012,
	7592,
	3646,
	6448,
	7461,
	8087,
	6745,
	8111,
	7491,
	7158,
	7855,
	7437,
	7392,
	5960
]

const INSTANCES_WITH_ATTENDANTS_BANNER: number[] = [
	6956,
	3109,
	3168,
	8278,
	4358,
	5237,
	6858,
	7879,
	2836,
	3845,
	2969,
	7515,
	8093,
	8360,
	4449,
	3110,
	8240,
	8234,
	8423,
	2175,
	4227,
	6895,
	8414,
	7689,
	7923,
	8343,
	2719,
	3674,
	5308,
	5383,
	5631,
	6094,
	4756,
	4363,
	4649,
	6942,
	7765,
	6809,
	7157,
	8398,
	7567,
	7991,
	7944,
	2804,
	2419,
	2788,
	3230,
	3552,
	4799,
	5185,
	3799,
	6400,
	8070,
	3089,
	3934,
	5135,
	5229,
	3273,
	4374,
	6109
]

const INSTANCES_WITH_MESSAGE_BANNER: number[] = [
	5946,
	6022,
	7603,
	4941,
	6061,
	7295,
	8239,
	5768,
	8117
]

const INSTANCES_WITH_ICP_INTERVIEW_BANNER: number[] = [
	6067,
	5032,
	4227,
	3333,
	4316,
	6291,
	6726,
	3841,
	6340,
	4378,
	6593,
	2764,
	2764,
	3812,
	2202,
	8272,
	8273,
	6222,
	4979,
	7880,
	7955,
	3430,
	3200,
	3818,
	1779,
	3230,
	4064,
	6245,
	4649,
	5413,
	5322,
	3211,
	3211,
	3674,
	2465,
	8358,
	6387,
	6603,
	7815,
	5340,
	7234,
	7283,
	7325,
	3609,
	2773,
	6757,
	5730,
	7230,
	4968,
	1969,
	7991,
	6915,
	7025,
	2221,
	7226,
	3624,
	5989,
	3095,
	3610,
	5768,
	5355,
	6942,
	7440,
	5383,
	6323,
	7070,
	7007,
	6115,
	2019,
	6710,
	2399,
	3503,
	1776,
	2733,
	6860,
	6592,
	3695,
	5093,
	2692,
	2264,
	7221,
	3777,
	6361,
	6615,
	2716,
	7181,
	6061,
	2558,
	6530,
	4143,
	6435,
	5316,
	2541,
	6706,
	7264,
	3067,
	4947,
	4971,
	6059,
	7217,
	7349,
	7207,
	5287,
	5283,
	5082,
	6195,
	3072,
	7080,
	7087,
	5978,
	6673,
	2443,
	4941,
	6809,
	7158,
	6531,
	5330,
	6430,
	2352,
	3810,
	6007,
	6037,
	5314,
	5606,
	6976,
	3403,
	4714,
	7703,
	8184,
	4097,
	3727,
	6858,
	3543,
	6584,
	2717,
	3352,
	5135,
	6900,
	5388,
	7179,
	7157,
	6823,
	6855,
	4525,
	5401,
	3491,
	6243,
	2714,
	7382,
	2739,
	8311,
	8147,
	8142,
	8133,
	3473,
	8265,
	7496,
	6834,
	1913,
	6755,
	3150,
	2225,
	6022,
	7345,
	7261,
	2549,
	7379,
	6815,
	5946,
	7376,
	8088,
	8083,
	7593,
	2882,
	1855,
	6895,
	3109,
	2447,
	3906,
	3827,
	4878,
	2836,
	2512,
	7137,
	3355,
	6678,
	6678,
	4893,
	2421,
	6444,
	8038,
	2278,
	3032,
	2515,
	5716,
	6811,
	2979,
	6934,
	7256,
	6356,
	6537,
	7372,
	5226,
	4242,
	5828,
	7101,
	4698,
	7091,
	6520,
	2173,
	5799,
	5931,
	5931,
	5932,
	5932,
	2518,
	3303,
	6946,
	6863,
	8107,
	6826,
	7399,
	7057,
	7194,
	5282,
	6626,
	6385,
	7251,
	2608,
	3934,
	3081,
	6297,
	6312,
	3372,
	2866,
	3908,
	4535,
	3890,
	3377,
	3215,
	6345,
	2873,
	2874,
	3245,
	7916,
	6880,
	5523,
	6383,
	6382,
	7996,
	7715,
	7836,
	2091,
	5678,
	6353,
	4241,
	7240,
	5811,
	6980,
	5670,
	7200,
	7870,
	8258,
	2969,
	4142,
	8252,
	7060,
	2284,
	6759,
	5183,
	7307,
	5326,
	5481,
	7237,
	5386,
	7004,
	4953,
	5239,
	7341,
	8240,
	8239,
	5371,
	1729,
	1999,
	5929,
	3209,
	2753,
	4731,
	7039,
	7013,
	7098,
	7312,
	7295,
	7315,
	5734,
	5919,
	7109,
	6888,
	2966,
	7327,
	5631,
	6956,
	2109,
	6185,
	3658,
	3715,
	6874,
	7302,
	5336,
	5727,
	4519,
	7299,
	4939,
	7336,
	5812,
	5812,
	2659,
	5677,
	2446,
	6455,
	4643,
	2561,
	2175,
	3191,
	2896,
	8422,
	5362,
	6355,
	7073,
	6930,
	6448,
	6719,
	7012,
	7407,
	7291,
	7238,
	7152,
	2222,
	5801,
	5300,
	5991,
	7006,
	8352,
	6328,
	5214,
	6658,
	6049,
	4676,
	7069,
	4743,
	6628,
	6076,
	5995,
	6709,
	3872,
	2760,
	4094,
	6756,
	7017,
	4990,
	7433,
	7313,
	2763,
	7143,
	6205,
	2797,
	6569,
	6569,
	4524,
	5542,
	7513,
	3656,
	7897,
	8193,
	8198,
	8196,
	8192,
	8197,
	7790,
	7906,
	8331,
	8332,
	8009,
	7789,
	7898,
	7903,
	7998,
	7999,
	7893,
	7905,
	7904,
	8008,
	7908,
	8001,
	8002,
	8010,
	8000,
	8004,
	8011,
	8190,
	7910,
	7901,
	7907,
	8006,
	8191,
	8189,
	7896,
	8328,
	7909,
	7888,
	7895,
	7900,
	7894,
	7788,
	8003,
	8329,
	7899,
	7892,
	8007,
	8005,
	8195,
	7902,
	7997,
	8330,
	7792,
	7204,
	2486,
	3311,
	8148,
	7065,
	1983,
	5211,
	8124,
	8125,
	8123,
	7348,
	8228,
	8407,
	8319,
	4756,
	6876,
	7035,
	6953,
	3151,
	3646,
	7077,
	6856,
	6334
]

const INSTANCES_WITH_BOTS_BANNER: number[] = [
	2230,
	7307,
	3028,
	6902,
	2046,
	5978,
	8068,
	3377,
	6426,
	7736,
	7086,
	7087,
	7388,
	6592,
	6746
]

const INSTANCES_WITH_DEFAULT_BANNER: number[] = [
	7281,
	6412,
	8108,
	3676,
	3754,
	4864,
	5682,
	5284,
	8274,
	8170,
	8346,
	7434,
	7441,
	8168,
	8365,
	7548,
	6729,
	8416,
	7626,
	7743,
	8072,
	2446,
	5543,
	7025,
	7766,
	8396,
	6404,
	6567,
	6744,
	6757,
	7069,
	8379,
	4833,
	8408,
	8420,
	7651,
	7382,
	7486,
	7550,
	8134,
	8427,
	6673,
	8395,
	8445,
	8417,
	7783,
	8054,
	8161,
	8214,
	8277,
	8016,
	8057,
	8342,
	8353,
	2364,
	2421,
	5192,
	7834,
	7226,
	7968,
	8210,
	8282,
	8281,
	8276,
	7960,
	7953,
	8037,
	8357,
	8362,
	3771,
	3958,
	4651,
	4774,
	4951,
	4964,
	5096,
	5168,
	5310,
	5341,
	5724,
	6425,
	6227,
	7852,
	7853,
	7866,
	7270,
	8421,
	8432,
	8135,
	7149,
	6553,
	6682,
	6980,
	6932,
	7097,
	6838,
	6380,
	8387,
	8452,
	6293,
	8406,
	8405,
	6510,
	8439,
	6418,
	8255,
	7794,
	7657,
	7924,
	8309,
	8275,
	8431,
	7957,
	8028,
	8086,
	8105,
	4405,
	5074,
	5161,
	5246,
	5256,
	5359,
	5387,
	5392,
	5424,
	5475,
	5589,
	5633,
	5902,
	5951,
	6638,
	6776,
	7043,
	7049,
	6770,
	6846,
	5982,
	6123,
	6562,
	5715,
	5714,
	6799,
	6718,
	6851,
	6883,
	6897,
	6995,
	6073,
	3656,
	3811,
	8083,
	8088,
	6629,
	5266,
	8185,
	2630,
	6996,
	8226,
	7865,
	6111,
	6548,
	5845,
	3909,
	7916,
	8324,
	5242,
	5674,
	8042,
	3853,
	7374,
	7535,
	2125,
	8140,
	3732,
	5335,
	8015,
	5320,
	6994,
	2706,
	8410,
	5544,
	8204,
	8245,
	8349,
	4957,
	6861,
	8236,
	2838,
	5516,
	5621,
	5721,
	5421,
	3572,
	8018,
	8380,
	7718,
	8026,
	6997,
	7739,
	3901,
	4414,
	5418,
	7312,
	5165,
	7947,
	6462,
	6873,
	5325,
	5376,
	8136,
	7616,
	5060,
	5661,
	7547,
	6917,
	4378,
	5087,
	5263,
	5406,
	5627,
	4956,
	8063,
	7379,
	8338
]

const INSTANCES_WITH_PAYMENT_RECURRENCE_MIGRATION_BANNER: number[] = [
	6744,
	6592,
	6567,
	2628,
	4941,
	6448,
	6073,
	4864,
	2399,
	6400,
	6094,
	6022,
	6061,
	5989,
	5946,
	5965,
	5978,
	6111,
	5631,
	5674,
	4968,
	6012,
	5523,
	5237,
	5226,
	5239,
	5340,
	5308,
	4953,
	3727,
	7434,
	7440,
	7307,
	7379,
	7237,
	7208,
	7295,
	7157,
	3503
]

const INSTANCES_WITH_NOT_SEARCH_BASE_BANNER: number[] = [
	7477,
	8378,
	7098,
	8376,
	5183,
	5371,
	7937,
	7964,
	8402,
	8344,
	3377,
	6815,
	6709,
	5330,
	7669,
	8567,
	8519
]

export const INSTANCES_WITH_MERLIN_WARNING_DEADLINE_IN_29_07_2026: number[] = [
	9056, 9077, 9094, 9080, 9092, 9093, 9095, 9096, 7917, 9100, 9071, 6754, 9105
]

export const INSTANCES_WITH_MERLIN_WARNING_DEADLINE_IN_15_09_2026: number[] = [
	8042, 9132, 9137, 9148, 9153
]

const WEB_INSTANCES_TO_SHOW_BLOCK_WHATSAPP_BANNER_AND_ACCOUNT_MANAGER_POPUP: number[] = [
	9183, 9165, 9139, 9138, 9126, 9125, 9116, 9072, 9051, 9011, 9005, 8985,
	8972, 8971, 8939, 8936, 8933, 8925, 8914, 8913, 8907, 8885, 8881, 8866,
	8846, 8839, 8837, 8798, 8795, 8786, 8785, 8784, 8781, 8776, 8775, 8774,
	8737, 8728, 8725, 8722, 8720, 8717, 8704, 8699, 8698, 8697, 8696, 8690,
	8687, 8674, 8673, 8667, 8664, 8663, 8649, 8639, 8636, 8630, 8621, 8614,
	8613, 8612, 8608, 8605, 8602, 8601, 8597, 8596, 8584, 8572, 8568, 8566,
	8564, 8563, 8552, 8544, 8541, 8540, 8525, 8521, 8519, 8518, 8511, 8495,
	8494, 8489, 8484, 8476, 8466, 8456, 8452, 8425, 8415, 8414, 8373, 8371,
	8348, 8345, 8344, 8335, 8305, 8303, 8258, 8253, 8250, 8235, 8219, 8209,
	8208, 8207, 8206, 8203, 8200, 8177, 8160, 8145, 8125, 8106, 8102, 8099,
	8096, 8092, 8085, 8067, 8056, 8023, 7983, 7981, 7978, 7930, 7922, 7873,
	7871, 7870, 7855, 7836, 7816, 7803, 7781, 7769, 7753, 7751, 7746, 7729,
	7719, 7716, 7702, 7675, 7661, 7653, 7635, 7613, 7612, 7593, 7534, 7531,
	7516, 7504, 7496, 7477, 7468, 7433, 7405, 7378, 7315, 7313, 7264, 7240,
	7237, 7230, 7221, 7218, 7207, 7200, 7181, 7143, 7109, 7091, 7073, 7070,
	7060, 6880, 6876, 6874, 6863, 6860, 6826, 6825, 6823, 6815, 6759, 6754,
	6710, 6709, 6706, 6678, 6669, 6663, 6628, 6603, 6587, 6584, 6570, 6569,
	6564, 6520, 6455, 6444, 6435, 6430, 6420, 6373, 6361, 6353, 6291, 6262,
	6245, 6243, 6211, 6185, 6115, 6076, 6049, 6037, 6012, 6007, 5991, 5734,
	5727, 5678, 5670, 5606, 5413, 5355, 5337, 5336, 5314, 5300, 5295, 5283,
	5213, 4990, 4971, 4893, 4742, 4714, 4525, 4241, 4143, 4142, 4094, 3918,
	3906, 3872, 3818, 3777, 3727, 3624, 3543, 3372, 3355, 3352, 3333, 3303,
	3300, 3200, 3151, 3081, 2966, 2949, 2896, 2874, 2873, 2764, 2763, 2716,
	2618, 2601, 2558, 2549, 2541, 2518, 2465, 2447, 2352, 2222, 2221, 2091,
	1983, 1969, 1855
]

class HardCodedService {
	readonly instanceIdMap = {
		letalk: {
			MOTA: 1,
			RAFAEL_LIMA: 1712,
			GUSTAVO_LETALK: 17,
			CS_LETALK: 3411,
			JOHN_LETALK: 3046,
			RAYAN_LETALK: 2245,
			TONIM_LETALK: 3028,
			ISRAEL_LETALK: 7426,
			JU_LETALK: 5420,
			EVENTOS_LETALK: 3098,
			ANA_CS_LETALK: 3665,
			CHECK_REGISTROS: 2225,
			ASSINATURAS: 1745,
			CS: 7074,
			SUPORTE: 5644,
			SDR: 7153,
			CLOSER_VANESSA: 7168,
			CLOSER_ROGERIO: 7161,
			GUI_VILELA: 7324,
			TAGGAR: 7166,
			PEDRO_LUCENA: 7428,
			DOMINUS_COMUNICACAO: 2518,
			FABIANA_RODESKI: 3409,
			SAMUEL_TESTER_LETALK: 7566,
			SAMUEL_TESTER_LETALK_WABA: 7166,
			WELLINGTON_LETALK: 7427,
			RAFAEL_WABA: 7532,
			LET_WABA: 7524,
			PICSIZE: 7745,
			MATHEUS_LETALK: 7425,
			GUSTAVO_LETALK_WABA: 7608,
			MOTA_AGENTE_DE_IA: 8046,
			JESSICA_MARIANO_PARCERIAS: 7385,
			MARKETING_WABA: 7821,
			LETALK_WABA: 7524,
			LETALK_CS: 8807,
			PEDRO_VENDAS_LETALK: 7886,
			THAYNA_VENDAS_LETALK: 7168,
			AMANDA_VENDAS_LETALK: 8996,
			LETICIA_VENDAS_LETALK: 7161
		},
		client: {
			EVENTPLAY_GROWTH: 3090,
			THAIS_DESPACHANTE_DOK: 5391,
			DANIEL_NOGUEIRA: 2487,
			DURVAL_BATISTA_MOBLY: 3724,
			PEDRO_CARDOSO_ALLUGATOR: 3790,
			JENIFER_SANTOS_RBRANDSBR: 3303,
			NATUS_REVESTIMENTOS_LANTAI: 5632,
			PETRUS_LANTAI: 5611,
			GERENTE_REVESTIMENTO_SEM_OFERTA: 5496,
			LUCAS_CARVALHO_CHILE: 2397,
			RAPHAEL_LIMA_DUARTE: 3252,
			SISTEMAS_REFORNARI: 2130,
			SUHIANA_WEDOCTORS: 3334,
			MIGUEL_BRUNO: 4358,
			INFO_ROLEINROMA: 3290,
			FINANCIAL_MOVECRIPTO: 2534,
			MURILO_S_FALEIROS: 5233,
			SISTEMAS_LITORAL_VERDE: 2827,
			NATHAN_LETSEATIT: 5219,
			DAYONE_INTERCAMBIOS: 4796,
			CRIACAO_LANTAI: 5314,
			THIAGO_ADMIN_SCITRUS: 3943,
			MARKETING_INOXDINE: 4055,
			MARCELO_ELETECHSOLAR: 4084,
			LEONARDO_FONSECA_JR: 4535,
			ADMINISTRATIVO_ORAL_UNIC_CURITIBA: 5253,
			ROBSON_COSTA_SOCIEDADE_INDIVIDUAL_DE_ADVOCACIA: 6468,
			LOGISTICA_AVENTURA_OPERADORA_DE_TURISMO_LTDA: 6841,
			CONTATO_MDM: 6648,
			LETSEATIT: 5219,
			APEREAL: 3473,
			NERES_BARBEARIA: 6417,
			THALLERS_MILANIO: 7132,
			AM_ANDRADE: 7409,
			CS_FITX: 7703,
			QUARK_TEC: 5828,
			SUPORTE_FINANCIAL_MOVE: 3703,
			TRANS_BY_SHOP: 2069,
			BRUNO_MIGUEL: 4358,
			MANIA_GOURMET_FORMACAO_PROFISSIONAL: 7771,
			IMPERIO_DO_MARMORE: 3311,
			BALAIO_DE_FLORES: 7829,
			PABLO_WIEDEMANN: 7327,
			FIELD_CONTROL_LTDA: 7961,
			PROSELF_COMERCIO_DE_PRODUTOS_CAPILARES_LTDA: 7759,
			GABI_CERVANTES: 2019,
			FAMILIA_QUINDIM: 2717,
			MARKETING_PIMPAO: 3209,
			DRA_NATASHA: 3646,
			LOGIN_GERENCIAL_ACESSO_DE_GERENTES_SAC: 6520,
			LOGIN_GERENCIAL_ACESSO_DE_GERENTES_LARISSA: 7091,
			SAMUEL: 7013,
			ADRIANA_MANA: 7077,
			LUCAS_ROCHA_INSIDE_SALES: 7715,
			LUCAS_ROCHA_TIME_URBANO: 7996,
			JESSICA_VINHEDO_LACUSTRE: 8111,
			VA_DE_MORAES_COMERCIO_LTDA: 8143,
			VIVER_DE_VELAS: 5330,
			AGENCIA_ALDE_MARKETING: 5744,
			DALILA: 5801,
			SUPER_MAYOREO: 7039,
			LORENA: 7321,
			RIO_CALDEIRAS_COMERCIAL_LTDA: 7744,
			ALCANCE_COMUNICACAO_LTDA: 8218,
			AGENCIA_R8_MARKETING_DIGITAL: 8248,
			HENRIQUE_H2DIGITAL: 8260,
			SB_ESTRATEGIAS_E_VENDAS: 5762,
			SCULPTLIFE: 6658,
			VINSEL: 8266,
			IGO_PIZZAS: 4241,
			CCV: 8600,
			UNIMED: 6826,
			ANA_FRANZ: 8206,
			BUZZMONITOR_TECNOLOGIA: 8780,
			LUCAS_VEDANTA: 8902,
			ESTILO_HOME_CENTER: 8860,
			GEO_SEM_FRONTEIRAS: 8905,
			TRASTEVERE_ASSESSORIA: 9070,
			ATLANTIS_EMPREENDIMENTOS_TURISTICOS_LTDA: 9154,
			DIVERSI: 9166,
			RAVIERA_MOTORS_RMJR_COMERCIO_DE_VEICULOS: 9167,
			RAVIERA_MOTORS_RMJR_COMERCIO_DE_VEICULOS_3: 9168,
			INSTANCIA_9169: 9169,
			BAREINTERNATIONAL: 9171,
			SEMOGLEO: 9178,
			CONTABILIDADE_E_ADMINISTRACAO_DE_CONDOMINIOS: 9181,
			KMNO: 9201,
			DOMINUS_COMUNICACAO: 9205,
			MARCEKESS: 9199,
			GO_LOCADORA_FLORIANOPOLIS: 8478,
			VITA_DENTU_VARANAS: 9193,
			MICHELE_SP: 9231,
			VEFORTE: 9232,
			ROD_CRED: 9233,
			FEBRAPO: 9234,
			CLINICA_ON: 9235,
			DRA_ALINE_LISBOA: 9143,
			KOCHEN_AZEITES_SABORIZADOS: 9238,
			AUTOESCOLA_LAGO: 9225,
			EVOSEG: 9185,
			AF_BARBOSA_CIA_LTDA: 9247,
			SOUVENIR_PATRIOTA: 9256,
			OTOMED: 9036,
			SOUZACAMIN: 9244,
			GELOPOP: 9266,
			DEGEMA: 9276,
			AMERICAVIDROS: 9194,
			COELHOETACHY: 9274,
			MILENIUM_EMBALAGENS: 9282,
			FRANCOLEILOES: 9260,
			COMERCIAL_MALLET_LTDA: 9286,
			FINAFINEZZA: 9248,
			MADEIREIRAJP: 9204,
			CASADOADS: 8845,
			VITORGASPARTRADER: 9294,
			CASADOADS_2: 9295,
			INSTANCIA_9250: 9250,
			APRESENTA_ME_CLIENT_INSTANCE: 9162
		},
		/**
		 * Sync with inbox-api
		 */
		UNINASSAU: {
			"BOA VIAGEM - PSICO E ODONTO": 7788,
			"NUCLEO DE CURSOS - CLINICAS INTEGRADAS": 7789,
			"GARANHUNS - PSICO E FISIO": 7790,
			"GRACAS - ODONTOLOGIA": 7792,
			"PAULISTA - ODONTO E PSICO": 7861,
			"CAMPINA GRANDE - FISIO E PSICO": 7888,
			"CAMPINA GRANDE - ODONTOLOGIA": 7892,
			"JOAO PESSOA - ODONTOLOGIA": 7893,
			"CAMPINA GRANDE - VETERINARIA": 7894,
			"RIO DE JANEIRO - ODONTO E PSICO ": 7895,
			"MACEIO - ODONTOLOGIA": 7896,
			"MACEIO - PSICO E FISIO": 7897,
			"GUARULHOS - ODONTOLOGIA": 7898,
			"CACOAL - POLICLINICA": 7899,
			"ANANINDEUA - ODONTO": 7900,
			"SALVADOR -  PSICO E FISIO": 7901,
			"JUAZEIRO DO NORTE - CLINICA INTEGRADA": 7902,
			"VITORIA DA CONQUISTA - PSICOLOGIA": 7903,
			"ALCINDO - PSICO E NUTR": 7904,
			"GUARULHOS - PSICOLOGIA": 7905,
			"FORTALEZA DOROTEIAS - ODONTOLOGIA": 7906,
			"PARNAIBA - ODONTO, FISIO E PSICO": 7907,
			"VITORIA DA CONQUISTA - ODONTO E FISIO": 7908,
			"JOAO PESSOA - PSICO, NUTR E FISIO": 7909,
			"SALVADOR - ODONTOLOGIA": 7910,
			"BARREIRAS - POLICLINICA": 7997,
			"SAO LUIS - VETERINARIA": 7998,
			"CARUARU - ODONTO, PSICO, FISIO E NUTRI": 7999,
			"MOSSORO - ODONTO, PSICO E FISIO": 8000,
			"MARACANAU - PSICOLOGIA": 8001,
			"FORTALEZA DOROTEIAS - PSICO E FISIO": 8002,
			"NATAL - VETERINARIA ": 8003,
			"ALCINDO CACELA - ODONTOLOGIA": 8004,
			"GRACAS - PSICO E FISIO": 8005,
			"TERESINA REDENCAO - ODONTO, PSICO E NUTR": 8006,
			"OLINDA - PSICO, FISIO E ODONTO": 8007,
			"FORTALEZA DOROTEIAS - VETERINARIA": 8008,
			"SAO LUIS - PSICO E FISIO": 8009,
			"JOAO PESSOA - VETERINARIA": 8010,
			"UNI7 FORTALEZA - PSICOLOGIA": 8011,
			"Instância de THAÍS MARQUES": 8047,
			"SERRA TALHADA - CLINICA INTEGRADA": 8189,
			"GRACAS -  VETERINARIA ": 8190,
			"ITAQUAQUECETUBA - ODONTO E PSICO": 8191,
			"RIO DE JANEIRO - VETERINARIA ": 8192,
			"BRASILIA  - PSICOLOGIA": 8193,
			"Sem conexão 1": 8194,
			"MANAUS  - FISIO, PSICO E NUTRI": 8195,
			"Sem conexão 2": 8196,
			"MANAUS - VETERINARIA ": 8197,
			"MANAUS - ODONTOLOGIA": 8198,
			"JUAZEIRO DO NORTE - VETERINARIA": 8328,
			"VILHENA - POLICLINICA ": 8329,
			"NATAL - FISIO- PSICO-ODONTO-ESTETICA": 8330,
			"CACOAL - VETERINARIA": 8331,
			"GENTIL - ODONTO - FISIO - PSICO": 8332,
			"Rayan teste": 2245,
			"PETROLINA - CLINICA INTEGRADA": 8486
		}
	}

	private readonly userIdMap = {
		client: {
			THAIS_MARQUES_UNINASSAU: 21982,
			JOHN_LETALK: 6722,
			RAYAN_LETALK: 3566,
			GUI_LETALK: 6666
		}
	}

	private readonly adminIdMap = {
		CAROL: 2,
		GUSTAVO: 4,
		TONIM: 5,
		GABRIELA_SUPORTE: 12,
		MOTA: 14,
		JHON: 20,
		LAURA_SUPORTE: 23,
		LETICIA: 24,
		PEDRO_SUPORTE: 30,
		RAYAN: 31,
		RAFAEL_LIMA: 32,
		RENATO_SUPORTE: 33,
		SAMUEL_SUPORTE: 36,
		VITORIA_FEROLA: 42,
		ANA_SUPORTE: 43,
		MARIANA: 47,
		DANDARA: 49,
		SAMUEL_AMPARO: 54,
		JESSICA_MARIANO: 59,
		GUI: 1,
		LHUANA: 52,
		YASMIN_MELGACO: 63,
		RAFAELA_JOSINO: 67,
		SABRINA_LOPES: 61,
		MIGUEL_COELHO: 70,
		THAUANA: 72,
		GABRIEL_AMARAL: 74,
		MARIA_CLARA: 69,
		POLIANA: 75,
		MATHEUS_DEV: 60,
		ISRAEL_DEV: 66,
		BRIAN_DEV: 51,
		ASSINATURAS: 80,
		FINANCEIRO: 11,
		SUELYN: 78,
		MARIA_SUYANY: 82,
		LEONARDO: 81,
		JOAO_PEDRO: 84,
		LAVINIA_CHARRUA: 85,
		IASMIN_TORRES: 86,
		GABRIELA_MEDEIROS: 87,
		IGOR_BEZERRA: 88
	}

	private readonly featureFlagMap: FeatureFlagMap = {
		instance: {
			canForwardMessages: [7166, 7385],
			canViewQueueMetrics: [
				...LETALK_WEB_TEST_INSTANCES,
				...LETALK_WABA_TEST_INSTANCES
			],
			canSendExtraDataInTheSendWebhookBlock: [
				this.instanceIdMap.letalk.ISRAEL_LETALK,
				this.instanceIdMap.client.UNIMED,
				this.instanceIdMap.client.RAVIERA_MOTORS_RMJR_COMERCIO_DE_VEICULOS_3
			],
			canValidateWaitForClientResponseBlockVariableName: [
				this.instanceIdMap.client.GO_LOCADORA_FLORIANOPOLIS,
				this.instanceIdMap.letalk.RAFAEL_WABA
			],
			canShowExportMessageOption: [
				this.instanceIdMap.letalk.ISRAEL_LETALK,
				this.instanceIdMap.letalk.RAYAN_LETALK,
				this.instanceIdMap.client.BUZZMONITOR_TECNOLOGIA,
				this.instanceIdMap.client.ESTILO_HOME_CENTER,
				this.instanceIdMap.client.INSTANCIA_9250
			],
			groupMentions: [
				this.instanceIdMap.letalk.GUSTAVO_LETALK,
				this.instanceIdMap.letalk.MOTA
			],
			canViewTrackableLinkInteractionsFilters: [
				this.instanceIdMap.letalk.RAFAEL_WABA
			],
			managerTagsIntegrationText: [
				this.instanceIdMap.letalk.GUSTAVO_LETALK,
				this.instanceIdMap.letalk.TONIM_LETALK,
				this.instanceIdMap.letalk.MOTA
			],
			isAbleToSeeStatusMessageResponse: [
				this.instanceIdMap.letalk.GUSTAVO_LETALK,
				this.instanceIdMap.letalk.TONIM_LETALK,
				this.instanceIdMap.letalk.MOTA
			],
			createChatBotByIntegration: [
				...LETALK_WEB_TEST_INSTANCES,
				this.instanceIdMap.letalk.TONIM_LETALK,
				this.instanceIdMap.letalk.GUSTAVO_LETALK,
				this.instanceIdMap.letalk.JU_LETALK,
				this.instanceIdMap.letalk.MOTA
			],
			canRetrySocketEventEmissionAfterReconnection: [
				this.instanceIdMap.client.PABLO_WIEDEMANN
			],
			datadogTracing: [],
			datadogLogs: [],
			canIgnoreMessageBlastAdvise: [
				...LETALK_WEB_TEST_INSTANCES,
				this.instanceIdMap.letalk.DOMINUS_COMUNICACAO,
				this.instanceIdMap.letalk.FABIANA_RODESKI
			],
			canFilterOthersTabWithLastMessageTransactedIn4Hours: [
				this.instanceIdMap.client.LUCAS_VEDANTA,
				this.instanceIdMap.client.RAVIERA_MOTORS_RMJR_COMERCIO_DE_VEICULOS,
				this.instanceIdMap.client.ATLANTIS_EMPREENDIMENTOS_TURISTICOS_LTDA
			],
			canFilterOthersTabWithLastMessageTransactedIn16Hours: [
				this.instanceIdMap.client.MANIA_GOURMET_FORMACAO_PROFISSIONAL
			],
			canFilterOnGoingAndQueueTabWithLastMessageTransactedIn16Hours: [
				this.instanceIdMap.client.MANIA_GOURMET_FORMACAO_PROFISSIONAL
			],
			canFilterOnGoingAndQueueTabWithLastMessageTransactedIn24Hours: [
				this.instanceIdMap.client.NERES_BARBEARIA,
				this.instanceIdMap.client.THALLERS_MILANIO,
				this.instanceIdMap.client.AM_ANDRADE,
				this.instanceIdMap.client.QUARK_TEC,
				this.instanceIdMap.client.TRANS_BY_SHOP
			],
			canFilterOnGoingAndQueueTabWithLastMessageTransactedIn72Hours: [
				this.instanceIdMap.client.BRUNO_MIGUEL
			],
			canFilterOnGoingAndQueueTabWithLastMessageTransactedIn96Hours: [
				this.instanceIdMap.client.PROSELF_COMERCIO_DE_PRODUTOS_CAPILARES_LTDA
			],
			canViewThumbnail: [
				this.instanceIdMap.letalk.TONIM_LETALK
			],
			canSeeMessageBlastTrigger: [
				...Object.values(this.instanceIdMap.UNINASSAU)
			],
			playgroundAIBetaTesters: [
				...PLAYGROUND_AI_BETA_TESTERS_INSTANCES
			],
			canShowActiveCampaignBanner: [
				...INSTANCES_WITH_ACTIVE_CAMPAIGN_BANNER
			],
			canShowAttendantsBanner: [
				...INSTANCES_WITH_ATTENDANTS_BANNER
			],
			canShowBotBanner: [
				...INSTANCES_WITH_BOTS_BANNER
			],
			canShowBulkContactsBanner: [
				...INSTANCES_WITH_BULK_CONTACTS_BANNER
			],
			canShowDefaultBanner: [
				...INSTANCES_WITH_DEFAULT_BANNER
			],
			canShowICPBanner: [
				...INSTANCES_WITH_ICP_INTERVIEW_BANNER
			],
			canShowMessagesBanner: [
				...INSTANCES_WITH_MESSAGE_BANNER
			],
			canShowPaymentRecurrenceMigrationBanner: [
				...INSTANCES_WITH_PAYMENT_RECURRENCE_MIGRATION_BANNER
			],
			canNotViewSearchBaseBanner: [
				...INSTANCES_WITH_NOT_SEARCH_BASE_BANNER
			],
			canDownloadReportFromMultiInstances: [
				this.instanceIdMap.letalk.MATHEUS_LETALK,
				this.instanceIdMap.letalk.TONIM_LETALK,
				this.instanceIdMap.letalk.RAYAN_LETALK,
				this.instanceIdMap.letalk.GUI_VILELA,
				this.instanceIdMap.letalk.JOHN_LETALK,
				this.instanceIdMap.letalk.GUSTAVO_LETALK,
				...Object.values(this.instanceIdMap.UNINASSAU),
				...LETALK_WEB_TEST_INSTANCES
			],
			canViewPartnershipsPage: [
				this.instanceIdMap.letalk.JESSICA_MARIANO_PARCERIAS,
				this.instanceIdMap.letalk.MARKETING_WABA,
				this.instanceIdMap.letalk.JOHN_LETALK
			],
			canReplyToButtons: [
				this.instanceIdMap.letalk.JOHN_LETALK,
				this.instanceIdMap.letalk.MATHEUS_LETALK,
				...LETALK_WEB_TEST_INSTANCES
			],
			canActivePushNotifications: [
				...LETALK_WABA_TEST_INSTANCES,
				...LETALK_WEB_TEST_INSTANCES,
				this.instanceIdMap.letalk.LETALK_CS,
				this.instanceIdMap.letalk.LETALK_WABA,
				this.instanceIdMap.client.GEO_SEM_FRONTEIRAS
			],
			canManageWABAChannelConnection: [
				this.instanceIdMap.letalk.TAGGAR
			],
			canUseBotFlowWABAErrorOutput: [8686],
			canShowWelcomePopup: [
				this.instanceIdMap.letalk.JOHN_LETALK,
				this.instanceIdMap.letalk.ISRAEL_LETALK
			],
			canUseRolePermissions: [
				this.instanceIdMap.letalk.TONIM_LETALK,
				this.instanceIdMap.letalk.JOHN_LETALK,
				this.instanceIdMap.letalk.TAGGAR
			],
			canAutoFinishAttendancesOnUserDeletion: [
				...LETALK_WEB_TEST_INSTANCES
			],
			canUseWABATemplateCreditsLimit: [
				...LETALK_WABA_TEST_INSTANCES,
				this.instanceIdMap.client.DIVERSI,
				this.instanceIdMap.client.BAREINTERNATIONAL,
				this.instanceIdMap.client.SEMOGLEO,
				this.instanceIdMap.client.CONTABILIDADE_E_ADMINISTRACAO_DE_CONDOMINIOS,
				this.instanceIdMap.client.KMNO,
				this.instanceIdMap.client.DOMINUS_COMUNICACAO,
				this.instanceIdMap.client.MARCEKESS,
				this.instanceIdMap.client.VITA_DENTU_VARANAS,
				this.instanceIdMap.client.MICHELE_SP,
				this.instanceIdMap.client.VEFORTE,
				this.instanceIdMap.client.ROD_CRED,
				this.instanceIdMap.client.FEBRAPO,
				this.instanceIdMap.client.CLINICA_ON,
				this.instanceIdMap.client.DRA_ALINE_LISBOA,
				this.instanceIdMap.client.KOCHEN_AZEITES_SABORIZADOS,
				this.instanceIdMap.client.AUTOESCOLA_LAGO,
				this.instanceIdMap.client.EVOSEG,
				this.instanceIdMap.client.AF_BARBOSA_CIA_LTDA,
				this.instanceIdMap.client.SOUVENIR_PATRIOTA,
				this.instanceIdMap.client.OTOMED,
				this.instanceIdMap.client.SOUZACAMIN,
				this.instanceIdMap.client.GELOPOP,
				this.instanceIdMap.client.DEGEMA,
				this.instanceIdMap.client.AMERICAVIDROS,
				this.instanceIdMap.client.COELHOETACHY,
				this.instanceIdMap.client.MILENIUM_EMBALAGENS,
				this.instanceIdMap.client.FRANCOLEILOES,
				this.instanceIdMap.client.COMERCIAL_MALLET_LTDA,
				this.instanceIdMap.client.FINAFINEZZA,
				this.instanceIdMap.client.MADEIREIRAJP,
				this.instanceIdMap.client.CASADOADS,
				this.instanceIdMap.client.VITORGASPARTRADER,
				this.instanceIdMap.client.CASADOADS_2,
				this.instanceIdMap.client.APRESENTA_ME_CLIENT_INSTANCE
			],
			canUseRDStationCRM: [
				this.instanceIdMap.client.TRASTEVERE_ASSESSORIA,
				this.instanceIdMap.letalk.TONIM_LETALK
			],
			canShowMerlinNonOwnerWarning: [
				...INSTANCES_WITH_MERLIN_WARNING_DEADLINE_IN_29_07_2026,
				...INSTANCES_WITH_MERLIN_WARNING_DEADLINE_IN_15_09_2026
			],
			canShowWhatsappBlockAlertBanner: [
				...WEB_INSTANCES_TO_SHOW_BLOCK_WHATSAPP_BANNER_AND_ACCOUNT_MANAGER_POPUP
			],
			canUseOutboundWebhooks: [
				...LETALK_WEB_TEST_INSTANCES,
				this.instanceIdMap.client.SEMOGLEO
			],
			canUseSuperlogicaBlock: [
				this.instanceIdMap.letalk.TAGGAR,
				this.instanceIdMap.client.CONTABILIDADE_E_ADMINISTRACAO_DE_CONDOMINIOS
			],
			canUseAccurateAttendantFilterLogic: [this.instanceIdMap.client.BAREINTERNATIONAL]
		},
		admin: {
			contactFlowHistory: [
				this.adminIdMap.TONIM,
				this.adminIdMap.JHON
			],
			messageProcessingSteps: [
				this.adminIdMap.ANA_SUPORTE,
				this.adminIdMap.JHON,
				this.adminIdMap.GUSTAVO,
				this.adminIdMap.MOTA,
				this.adminIdMap.ANA_SUPORTE,
				this.adminIdMap.LAURA_SUPORTE,
				this.adminIdMap.PEDRO_SUPORTE,
				this.adminIdMap.RENATO_SUPORTE,
				this.adminIdMap.SAMUEL_SUPORTE,
				this.adminIdMap.VITORIA_FEROLA,
				this.adminIdMap.GABRIELA_SUPORTE
			],
			copyRawInviteLink: [
				this.adminIdMap.JHON,
				this.adminIdMap.SAMUEL_SUPORTE
			],
			instanceResponsiblePhoneNumber: [
				this.adminIdMap.RAYAN,
				this.adminIdMap.LETICIA,
				this.adminIdMap.CAROL,
				this.adminIdMap.DANDARA,
				this.adminIdMap.MARIANA,
				this.adminIdMap.GABRIELA_SUPORTE
			],
			canConfigureWABAApiChannel: [
				this.adminIdMap.TONIM,
				this.adminIdMap.MOTA,
				this.adminIdMap.SAMUEL_SUPORTE,
				this.adminIdMap.LETICIA,
				this.adminIdMap.ANA_SUPORTE,
				this.adminIdMap.RAFAEL_LIMA,
				this.adminIdMap.JESSICA_MARIANO,
				this.adminIdMap.PEDRO_SUPORTE,
				this.adminIdMap.GUSTAVO,
				this.adminIdMap.GUI,
				this.adminIdMap.LHUANA,
				this.adminIdMap.YASMIN_MELGACO,
				this.adminIdMap.RAFAELA_JOSINO,
				this.adminIdMap.SABRINA_LOPES,
				this.adminIdMap.MIGUEL_COELHO,
				this.adminIdMap.VITORIA_FEROLA,
				this.adminIdMap.JESSICA_MARIANO,
				this.adminIdMap.THAUANA,
				this.adminIdMap.GABRIEL_AMARAL,
				this.adminIdMap.GABRIELA_MEDEIROS,
				this.adminIdMap.MATHEUS_DEV,
				this.adminIdMap.ISRAEL_DEV,
				this.adminIdMap.JHON,
				this.adminIdMap.BRIAN_DEV,
				this.adminIdMap.LAURA_SUPORTE,
				this.adminIdMap.SUELYN,
				this.adminIdMap.MARIA_SUYANY,
				this.adminIdMap.LEONARDO,
				this.adminIdMap.IASMIN_TORRES,
				this.adminIdMap.JOAO_PEDRO,
				this.adminIdMap.LAVINIA_CHARRUA,
				this.adminIdMap.IGOR_BEZERRA
			],
			canIgnoreAttendantQuantityLimit: [
				this.adminIdMap.GUSTAVO,
				this.adminIdMap.JHON,
				this.adminIdMap.RAYAN,
				this.adminIdMap.GABRIELA_SUPORTE,
				this.adminIdMap.DANDARA,
				this.adminIdMap.LETICIA,
				this.adminIdMap.SAMUEL_AMPARO,
				this.adminIdMap.SAMUEL_SUPORTE,
				this.adminIdMap.ASSINATURAS,
				this.adminIdMap.FINANCEIRO
			],
			canImportBotsOnWaba: [
				this.adminIdMap.RENATO_SUPORTE,
				this.adminIdMap.JHON,
				this.adminIdMap.RAFAELA_JOSINO,
				this.adminIdMap.SABRINA_LOPES,
				this.adminIdMap.MIGUEL_COELHO,
				this.adminIdMap.VITORIA_FEROLA,
				this.adminIdMap.JESSICA_MARIANO,
				this.adminIdMap.ANA_SUPORTE,
				this.adminIdMap.GUSTAVO,
				this.adminIdMap.MARIA_CLARA,
				this.adminIdMap.POLIANA,
				this.adminIdMap.GABRIEL_AMARAL,
				this.adminIdMap.GABRIELA_MEDEIROS,
				this.adminIdMap.THAUANA,
				this.adminIdMap.IASMIN_TORRES
			],
			canShowExportMessageOption: [
				...Object.values(this.adminIdMap)
			],
			canViewAgentDebugInfo: [
				...Object.values(this.adminIdMap)
			],
			canAdminImportTags: [],
			canAdminImportQuickReplies: []
		},
		user: {
			canImportTags: [
				this.userIdMap.client.JOHN_LETALK,
				this.userIdMap.client.THAIS_MARQUES_UNINASSAU,
				this.userIdMap.client.RAYAN_LETALK,
				this.userIdMap.client.GUI_LETALK
			],
			canImportQuickReplies: [
				this.userIdMap.client.JOHN_LETALK,
				this.userIdMap.client.RAYAN_LETALK,
				this.userIdMap.client.GUI_LETALK,
				this.userIdMap.client.THAIS_MARQUES_UNINASSAU
			],
			canViewUninassauFeaturePage: [
				this.userIdMap.client.THAIS_MARQUES_UNINASSAU,
				this.userIdMap.client.RAYAN_LETALK,
				this.userIdMap.client.GUI_LETALK
			]
		}
	}

	checkFeatureFlag (type: FeatureFlag): boolean {
		const instanceIds = this.featureFlagMap.instance[type as InstanceFeatureFlagType] || []
		const adminIds = this.featureFlagMap.admin[type as AdminFeatureFlagType] || []
		const userIds = this.featureFlagMap.user[type as UserFeatureFlagType] || []
		const hasInstanceFeatureFlag = instanceIds.includes(this.instanceId) || LETALK_WEB_TEST_INSTANCES.includes(this.instanceId) || LETALK_WABA_TEST_INSTANCES.includes(this.instanceId)
		const hasUserFeatureFlag = userIds.includes(this.userId)
		const hasAdminFeatureFlag = adminIds.includes(this.adminId)

		const hasFeatureFlag = hasInstanceFeatureFlag || hasAdminFeatureFlag || hasUserFeatureFlag

		return hasFeatureFlag
	}

	canPaginateBotChats (): boolean {
		return [
			this.instanceIdMap.letalk.TAGGAR,
			this.instanceIdMap.letalk.TONIM_LETALK,
			this.instanceIdMap.client.RAVIERA_MOTORS_RMJR_COMERCIO_DE_VEICULOS,
			this.instanceIdMap.client.INSTANCIA_9169,
			this.instanceIdMap.client.ATLANTIS_EMPREENDIMENTOS_TURISTICOS_LTDA
		].includes(this.instanceId)
	}

	isAdmin (): boolean {
		return Boolean(this.adminId)
	}

	get instanceId (): number {
		const instanceId = StorageService.get<string>(StorageService.reservedKeys.INBOX_INSTANCE_ID)

		return Number(instanceId)
	}

	private get userId (): number {
		const userId = StorageService.get<string>(StorageService.reservedKeys.INBOX_USER_ID)

		return Number(userId)
	}

	private get adminId (): number {
		const adminId = StorageService.get<string>(StorageService.reservedKeys.INBOX_ADMIN_ID)

		return Number(adminId)
	}
}

export default new HardCodedService()
